<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import Checkout from './Checkout.svelte';
    import { push } from "svelte-spa-router";
    import { auth } from "../js/auth.js";
    import { toast } from "../js/toast.js";
    import { api } from '../js/api.js';
    import { spinner } from "../js/spinner.js";
    import Profile from './Profile.svelte';
    import type { ApiResponse } from '../js/apiTypes.js';

    interface CartItem {
        uuid: string;
        name: string;
        description: string;
    }

    interface OrderLine {
        item: CartItem;
        quantity: number;
        unit_price_inc_tax: number;
    }

    interface Cart {
        order_lines: OrderLine[];
        total_inc_tax: number;
    }

    export let userId: string | null = null;
    let cart: Cart | null = null;
    let displayCheckout = false;
    let displayProfile: boolean = false;

    const unsubscribe = auth.subscribe($auth => {
        if ($auth.user && $auth.user.uuid) {
            userId = $auth.user.uuid;
        } else {
            push('/');
        }
    });

    async function checkout() {
        if (!cart || cart.total_inc_tax === 0) {
            return;
        }
        const response = await api.crudGet('user', $auth.user.uuid);
        if (!response.data.first_name || response.data.first_name === "null" || response.data.first_name.trim() === "") {
            toast.showNotification(false, 'Please complete your profile before proceeding to checkout');
            displayProfile = true;
        } else {
            displayCheckout = true;
        }
    }

    async function fetchCart(infoText = '') {
        if (!userId) return;
        setInfoText(infoText);
        spinner.show();
        const response = await api.getShoppingCart(userId) as unknown as ApiResponse<Cart>;
        spinner.hide();
        if (!response.success) {
            toast.showNotification(response.success, response.message || 'Failed to fetch cart');
            return;
        }
        cart = response.data ?? null;
    }

    async function changeItemQty(itemUuid: string, qty: number) {
        if (!userId) return;
        spinner.show();
        const response = await api.putShoppingCartLine(userId, itemUuid, qty) as unknown as ApiResponse<Cart>;
        spinner.hide();
        if (!response.success) {
            toast.showNotification(response.success, response.message || 'Failed to update cart');
            return;
        }
        cart = response.data ?? null;
    }

    async function deleteLine(itemUuid: string) {
        changeItemQty(itemUuid, 0);
    }

    function setInfoText(text: string) {
        const cartInfoDiv = document.getElementById('cart-info');
        if (cartInfoDiv) {
            cartInfoDiv.textContent = text;
        }
    }

    async function handlePaid() {
        fetchCart('Your order was submitted successfully.');
    }

    function handleProfileSaved() {
        auth.userRefresh();
        displayProfile = false;
        if (cart && cart.total_inc_tax > 0) {
            displayCheckout = true;
        }
    }

    onMount(() => {
        fetchCart('');
    });

    onDestroy(() => {
        unsubscribe();
    });
</script>

    <h2 class="ui header">Shopping Cart</h2>
    {#if cart && cart.order_lines.length != 0}
        <table class="ui compact table unstackable">
            <thead>
                <tr class="table-cart-tr">
                    <th class="three wide">Name</th>
                    <th>Description</th>
                    <th class="two wide">Quantity</th>
                    <th class="two wide right aligned">Unit Price</th>
                    <th class="one wide center aligned">Action</th>
                </tr>
            </thead>
            <tbody>
            {#each cart.order_lines as {item, quantity, unit_price_inc_tax}}
                <tr>
                    <td class="three wide">{item.name}</td>
                    <td>{item.description}</td>
                    <td class="ui form">
                        <div class="field">
                        <input
                            id={item.uuid}
                            type="number"
                            min="1"
                            bind:value={quantity}
                            class="table-cart-input-number"
                            on:input={() => changeItemQty(item.uuid, quantity)}
                            on:keydown={(e) => e.preventDefault()}/>
                        </div>
                    </td>
                    <td class="two wide right aligned">${unit_price_inc_tax}</td>
                    <td class="one wide center aligned">
                        <div class="ui icon tiny buttons">
                            <button on:click={() => deleteLine(item.uuid)} title="delete" class="ui button">
                                <i class="trash alternate outline icon"></i>
                            </button>
                        </div>
                    </td>
                </tr>
            {/each}
            </tbody>
            <tfoot>
                <tr>
                    <td colspan="3" class="right aligned">Total (Inc. Tax)</td>
                    <td colspan="2"><b>${cart.total_inc_tax}</b></td>
                </tr>
            </tfoot>
        </table>
        <div class="cart-info" id="cart-info"></div>
        <button class="ui right floated positive button" on:click|preventDefault={checkout} disabled={cart.total_inc_tax === 0}>Checkout</button>
    {:else}
        <div class="cart-info" id="cart-info"></div>
        <div class="ui icon message"><i class="info circle icon"></i><div class="content"><p>Your shopping cart is empty.</p></div></div>
    {/if}
<Profile isPopup={true} bind:displayProfile on:saved={handleProfileSaved} />
<Checkout bind:displayCheckout on:paid={handlePaid} />
